import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, getPublicSettings } from "@/lib/api";

export default function Register() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", mobile: "", password: "", referral_code: "" });
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(null);
  const [refLocked, setRefLocked] = useState(false);
  const [bonusAmt, setBonusAmt] = useState(0);
  const { register } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const ref = sp.get("ref");
    if (ref) { setF((p) => ({ ...p, referral_code: ref })); setRefLocked(true); }
  }, [sp]);

  useEffect(() => {
    api.get("/terms/active").then((r) => setTerms(r.data)).catch(() => {});
    getPublicSettings().then((d) => setBonusAmt(d.signup_bonus || 0)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...f, consent: agree });
      toast.success("Account created! Welcome to Credit Kendra.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="Get a permanent User ID and start earning."
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-emerald-brand">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {refLocked && bonusAmt > 0 && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3" data-testid="register-bonus-banner">
            <p className="text-sm font-bold text-teal-700">Signup &amp; Get ₹{bonusAmt} Bonus!</p>
            <p className="text-xs text-teal-600/80 mt-0.5">*T&C: Bonus tabhi milega jab aap earn karke pehla qualifying withdrawal karenge.</p>
          </div>
        )}
        {[["name", "Full Name", "text"], ["email", "Email", "email"]].map(([k, l, t]) => (
          <div key={k}>
            <Label className="font-semibold text-slate-600">{l}</Label>
            <Input type={t} required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`register-${k}`} className="mt-1.5 h-11" placeholder={l} />
          </div>
        ))}
        <div>
          <Label className="font-semibold text-slate-600">Password</Label>
          <div className="relative mt-1.5">
            <Input type={showPw ? "text" : "password"} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password" className="h-11 pr-11" placeholder="Password" />
            <button type="button" onClick={() => setShowPw(!showPw)} data-testid="register-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0B223D] transition-colors">
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="font-semibold text-slate-600">Mobile Number</Label>
          <div className="mt-1.5 flex">
            <span className="inline-flex items-center px-3 h-11 rounded-l-md border border-r-0 border-slate-200 bg-slate-100 text-slate-600 font-semibold text-sm">+91</span>
            <Input type="tel" required value={f.mobile} data-testid="register-mobile" maxLength={10}
              onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
              className="h-11 rounded-l-none" placeholder="10-digit mobile" />
          </div>
        </div>
        <div>
          <Label className="font-semibold text-slate-600">Referral code {refLocked ? "" : "(optional)"}</Label>
          <Input value={f.referral_code} onChange={(e) => setF({ ...f, referral_code: e.target.value })} data-testid="register-referral" readOnly={refLocked} className={`mt-1.5 h-11 font-mono-num ${refLocked ? "bg-teal-50 border-teal-200 text-teal-700 font-bold" : ""}`} placeholder="e.g. CK100002 or link ID" />
          {refLocked && <p className="text-xs text-teal-600 mt-1" data-testid="register-ref-locked">✓ Referral link se aapka code auto-fill ho gaya hai.</p>}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 max-h-48 overflow-y-auto text-xs text-slate-600 leading-relaxed whitespace-pre-line" data-testid="register-agreement-text">
          <p className="font-bold text-[#0B223D] mb-1.5 sticky top-0 bg-slate-50 pb-1">{terms?.title || "Partner Declaration & Terms"}{terms?.version ? ` (v${terms.version})` : ""}</p>
          {terms?.content || "Loading terms…"}
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="register-agreement-checkbox" className="mt-0.5" />
          <span>{terms?.checkbox_label || "I have read, understood and agree to the Partner Declaration & Terms. I confirm that I am an independent referral partner and will follow applicable laws, customer-consent requirements, campaign terms, and Credit Kendra policies."}</span>
        </label>
        <Button type="submit" disabled={loading || !agree} data-testid="register-submit-btn" className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
